/**
 * AccountPopover + AccountMenu - nut tai khoan tren NavRail (desktop) va
 * BottomTabBar (mobile). Thay the account footer cu cua chat Sidebar.
 *
 * Spec: docs/superpowers/specs/2026-06-12-telegram-liquid-glass-app-shell-design.md
 * muc 3.1. PDPA reachability (legal-upl C3): menu PHAI chua link /privacy +
 * /terms + DPO mailto vi Header/Footer bi route-gate off tren app routes.
 *
 * AccountMenu tach rieng (pure presentational) de SSR-test khong can jsdom.
 */
import { useEffect, useRef, useState } from 'react';
import { LogOutIcon, UserCircleIcon } from '../chat/icons';
import { LangSwitcher } from '../lang-switcher';
import { ThemeToggle } from '../theme-toggle';

export type AccountMenuProps = {
  lang: 'vi' | 'en';
  isAuthed: boolean;
  userName?: string | null;
  userEmail?: string | null;
  onLogout?: () => void;
};

export function AccountMenu({ lang, isAuthed, userName, userEmail, onLogout }: AccountMenuProps) {
  const display =
    userName?.trim() || userEmail?.trim() || (lang === 'vi' ? 'Ẩn danh' : 'Anonymous');
  return (
    <div className="asklaw-account__menu" role="menu">
      {isAuthed ? (
        <>
          <div className="asklaw-account__row asklaw-account__row--name">{display}</div>
          <a className="asklaw-account__item" href="/me" role="menuitem">
            <UserCircleIcon size={14} strokeWidth={1.6} />
            <span>{lang === 'vi' ? 'Tài khoản' : 'Account'}</span>
          </a>
        </>
      ) : (
        <a className="asklaw-account__item" href="/auth/login" role="menuitem">
          <UserCircleIcon size={14} strokeWidth={1.6} />
          <span>{lang === 'vi' ? 'Đăng nhập' : 'Sign in'}</span>
        </a>
      )}
      <div className="asklaw-account__row">
        <LangSwitcher variant="compact" />
        <ThemeToggle />
      </div>
      {isAuthed && onLogout && (
        <button type="button" className="asklaw-account__item" onClick={onLogout} role="menuitem">
          <LogOutIcon size={14} strokeWidth={1.6} />
          <span>{lang === 'vi' ? 'Đăng xuất' : 'Log out'}</span>
        </button>
      )}
      <nav
        className="asklaw-account__legal"
        aria-label={lang === 'vi' ? 'Liên kết pháp lý' : 'Legal links'}
      >
        <a href="/privacy">{lang === 'vi' ? 'Quyền riêng tư' : 'Privacy'}</a>
        <span aria-hidden="true">|</span>
        <a href="/terms">{lang === 'vi' ? 'Điều khoản' : 'Terms'}</a>
        <span aria-hidden="true">|</span>
        <a href="mailto:dpo@asklaw.vn">DPO</a>
      </nav>
    </div>
  );
}

export type AccountPopoverProps = AccountMenuProps & {
  /** 'rail' = menu mo sang phai (desktop NavRail). 'tab' = mo len tren (mobile tab bar). */
  placement: 'rail' | 'tab';
};

export function AccountPopover({
  lang,
  isAuthed,
  userName,
  userEmail,
  onLogout,
  placement,
}: AccountPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initial = (userName ?? userEmail ?? '?').trim().charAt(0).toUpperCase();
  const btnLabel = isAuthed
    ? lang === 'vi'
      ? 'Tài khoản'
      : 'Account'
    : lang === 'vi'
      ? 'Đăng nhập'
      : 'Sign in';

  return (
    <div className={`asklaw-account asklaw-account--${placement}`} ref={ref}>
      <button
        type="button"
        className="asklaw-account__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={lang === 'vi' ? 'Tài khoản và tùy chọn' : 'Account and options'}
        onClick={() => setOpen((v) => !v)}
      >
        {isAuthed ? (
          <span className="asklaw-account__avatar" aria-hidden="true">
            {initial}
          </span>
        ) : (
          <UserCircleIcon size={22} strokeWidth={1.6} />
        )}
        <span className="asklaw-account__label">{btnLabel}</span>
      </button>
      {open && (
        <AccountMenu
          lang={lang}
          isAuthed={isAuthed}
          userName={userName}
          userEmail={userEmail}
          onLogout={
            onLogout
              ? () => {
                  setOpen(false);
                  onLogout();
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
